import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { QRCodeCanvas } from "qrcode.react";
import {
  Linkedin, FileText, Download, Globe2, ArrowRight, QrCode, Mail,
  Share2, Copy, Check, Landmark, Sparkles, Database, Heart, Zap, Eye,
} from "lucide-react";
import { API } from "../../lib/constants";
import { trackEvent } from "../../lib/track";

// ——— Card config: edit links here ———
const CARD = {
  name: "Priyanka Jha",
  title: "Founder Researcher — AI Orientation Observatory",
  blurb: "Researcher and systems thinker working at the intersection of innovation, artificial intelligence, and societal progress. Her work centers on conscious innovation — the idea that technological evolution must align with human dignity, fairness, and collective well-being.",
  linkedin: "https://www.linkedin.com/in/priyankajh",
  email: "pjha17@rediffmail.com", // shown as "Email Priyanka" — address stays hidden; swap for official email later
  unPortalUrl: "https://share.google/ME9cHec9k0GgRQ8uY", // UN Digital Cooperation Portal entry
  papers: [
    {
      title: "The Conscious Innovation Index (CII): A 7-Dimension Framework for Measuring the Ethical Alignment of Artificial Intelligence",
      url: "https://papers.ssrn.com/sol3/papers.cfm?abstract_id=6492398",
      venue: "SSRN",
    },
    {
      title: "The Possibility Engine: A Data-Grounded Framework for Software-Hardware AI Convergence and Future Capability Mapping",
      url: "https://papers.ssrn.com/sol3/papers.cfm?abstract_id=6481799",
      venue: "SSRN",
    },
  ],
};

const ORIENT_RIBBON = "linear-gradient(90deg, #10b981 0%, #3b82f6 33%, #8b5cf6 66%, #ef4444 100%)";
const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString());

export default function PitchCard() {
  const [pulse, setPulse] = useState(null);
  const [copied, setCopied] = useState(false);
  const qrWrapRef = useRef(null);

  // /card.html carries the Open Graph tags (rich previews) and instantly redirects here
  const cardUrl = `${window.location.origin}/card.html`;

  useEffect(() => {
    trackEvent("pitch_card_view");
    axios.get(`${API}/pulse`).then(r => setPulse(r.data)).catch(() => {});
  }, []);

  const downloadQR = () => {
    const canvas = qrWrapRef.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = "aioo-pitch-card-qr.png";
    a.click();
    trackEvent("pitch_card_qr_download");
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(cardUrl);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = cardUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const nativeShare = () => {
    if (navigator.share) {
      navigator.share({ title: "AI Orientation Observatory — Priyanka Jha", url: cardUrl }).catch(() => {});
    } else {
      copyLink();
    }
  };

  const enterObservatory = () => {
    sessionStorage.setItem("aioo_entered", "1");
    window.location.hash = "";
    window.location.reload();
  };

  const stats = [
    { label: "AI entities mapped", value: fmt(pulse?.total_entities), icon: Database, color: "#818cf8", glow: "rgba(129,140,248,0.25)" },
    { label: "Research papers", value: fmt(pulse?.total_papers), icon: FileText, color: "#34d399", glow: "rgba(52,211,153,0.25)" },
    { label: "Companies", value: fmt(pulse?.total_companies), icon: Globe2, color: "#38bdf8", glow: "rgba(56,189,248,0.25)" },
    { label: "Policies tracked", value: fmt(pulse?.total_policies), icon: Landmark, color: "#fbbf24", glow: "rgba(251,191,36,0.25)" },
  ];

  const orientations = [
    { name: "Care", color: "#10b981", icon: Heart },
    { name: "Utility", color: "#3b82f6", icon: Zap },
    { name: "Creative", color: "#8b5cf6", icon: Sparkles },
    { name: "Extraction", color: "#ef4444", icon: Eye },
  ];

  return (
    <div className="min-h-screen bg-[#060913] text-slate-200 overflow-x-hidden relative" data-testid="pitch-card-page">
      {/* Orientation ribbon */}
      <div className="h-1.5 w-full" style={{ background: ORIENT_RIBBON }} />

      {/* Cosmic glow background */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full opacity-25 blur-3xl"
          style={{ background: "radial-gradient(circle, #6366f1 0%, transparent 70%)" }} />
        <div className="absolute top-1/3 -left-40 w-[420px] h-[420px] rounded-full opacity-20 blur-3xl"
          style={{ background: "radial-gradient(circle, #10b981 0%, transparent 70%)" }} />
        <div className="absolute bottom-0 right-1/4 w-[380px] h-[380px] rounded-full opacity-20 blur-3xl"
          style={{ background: "radial-gradient(circle, #8b5cf6 0%, transparent 70%)" }} />
        <svg className="absolute top-10 right-6 w-72 h-72 opacity-[0.15]" viewBox="0 0 200 200" fill="none">
          <ellipse cx="100" cy="100" rx="95" ry="38" stroke="#818cf8" strokeWidth="0.8" transform="rotate(-18 100 100)" />
          <ellipse cx="100" cy="100" rx="72" ry="26" stroke="#34d399" strokeWidth="0.6" transform="rotate(-18 100 100)" />
          <circle cx="100" cy="100" r="7" fill="#8b5cf6" />
        </svg>
      </div>

      <div className="relative max-w-3xl mx-auto px-5 py-10 md:py-14">

        {/* Identity */}
        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-white via-indigo-200 to-violet-300 bg-clip-text text-transparent" data-testid="card-observatory-title">
              AI Orientation Observatory
            </h1>
            <p className="text-lg md:text-xl font-semibold text-white mt-2" data-testid="card-name">{CARD.name}</p>
            <p className="text-sm md:text-base text-indigo-300 mt-0.5">Founder Researcher</p>
            <div className="flex gap-2 mt-3">
              {orientations.map(o => (
                <span key={o.name} className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border"
                  style={{ borderColor: `${o.color}55`, color: o.color, background: `${o.color}14` }}>
                  <o.icon className="w-2.5 h-2.5" /> {o.name}
                </span>
              ))}
            </div>
          </div>
          <div className="hidden sm:block p-2 bg-white rounded-xl shadow-[0_0_30px_rgba(129,140,248,0.35)]">
            <QRCodeCanvas value={cardUrl} size={96} level="M" data-testid="card-qr-inline" />
          </div>
        </div>

        <p className="text-sm md:text-base text-slate-400 leading-relaxed mb-8">{CARD.blurb}</p>

        {/* UN enlisting badge */}
        <div className="flex flex-wrap items-center gap-2 mb-10" data-testid="un-enlisted-badge">
          <span className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-xs font-medium text-sky-200 border border-sky-400/40 shadow-[0_0_20px_rgba(56,189,248,0.2)]"
            style={{ background: "linear-gradient(90deg, rgba(56,189,248,0.15), rgba(99,102,241,0.15))" }}>
            <Landmark className="w-3.5 h-3.5 text-sky-300" />
            {CARD.unPortalUrl ? (
              <a href={CARD.unPortalUrl} target="_blank" rel="noopener noreferrer" className="hover:underline">
                Enlisted: UN Digital Cooperation Portal — AI Dialogue Partnerships Hub
              </a>
            ) : (
              <>Enlisted: UN Digital Cooperation Portal — AI Dialogue Partnerships Hub</>
            )}
          </span>
        </div>

        {/* Live stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
          {stats.map((s) => (
            <div key={s.label} className="rounded-xl p-4 border bg-white/[0.03] transition-transform hover:-translate-y-0.5"
              style={{ borderColor: `${s.color}40`, boxShadow: `0 0 24px ${s.glow}` }}>
              <s.icon className="w-4 h-4 mb-2" style={{ color: s.color }} />
              <p className="text-xl font-bold text-white">{s.value}</p>
              <p className="text-[11px] text-slate-400 mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        {/* The pitch */}
        <div className="rounded-2xl p-[1.5px] mb-10" style={{ background: "linear-gradient(120deg, rgba(139,92,246,0.6), rgba(16,185,129,0.4), rgba(59,130,246,0.5))" }}>
          <div className="rounded-2xl bg-[#0a0f1f] p-5 md:p-6">
            <h2 className="text-base md:text-lg font-bold text-white mb-2 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-violet-400" /> What we measure
            </h2>
            <p className="text-sm text-slate-300 leading-relaxed">
              The Observatory maps <span className="text-emerald-300 font-medium">what humanity builds AI for</span> — classifying
              every app, device, company, policy and paper by its value orientation (care, utility, creative, extraction)
              and scoring ecosystems with the 7-dimension <span className="text-violet-300 font-medium">Conscious Innovation Index</span>.
              Five observatories, {fmt(pulse?.countries_count) || "200+"} countries, refreshed weekly by automated scrapes.
            </p>
          </div>
        </div>

        {/* Papers */}
        <h2 className="text-sm uppercase tracking-widest text-indigo-300/80 mb-3">Research & Papers</h2>
        <div className="space-y-3 mb-10" data-testid="card-papers">
          {CARD.papers.map((p, i) => (
            <a key={p.title} href={p.url} target={p.download ? undefined : "_blank"} rel="noopener noreferrer"
              download={p.download || undefined}
              onClick={() => trackEvent("pitch_card_paper_click")}
              className="flex items-start gap-3 rounded-xl border bg-white/[0.03] p-4 transition-all hover:bg-white/[0.06] hover:-translate-y-0.5 group"
              style={{ borderColor: ["#8b5cf640", "#3b82f640", "#10b98140"][i % 3] }}>
              {p.download
                ? <Download className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                : <FileText className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: ["#a78bfa", "#60a5fa"][i % 2] }} />}
              <div className="min-w-0">
                <p className="text-sm text-slate-200 group-hover:text-white leading-snug">{p.title}</p>
                <p className="text-[11px] text-slate-500 mt-1">{p.venue}</p>
              </div>
            </a>
          ))}
        </div>

        {/* Contact + actions */}
        <div className="flex flex-wrap gap-3 mb-12">
          <a href={CARD.linkedin} target="_blank" rel="noopener noreferrer" data-testid="card-linkedin-btn"
            onClick={() => trackEvent("pitch_card_linkedin")}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#0a66c2] text-white text-sm font-medium hover:brightness-110 transition shadow-[0_0_20px_rgba(10,102,194,0.4)]">
            <Linkedin className="w-4 h-4" /> Connect on LinkedIn
          </a>
          {CARD.email && (
            <a href={`mailto:${CARD.email}?subject=${encodeURIComponent("Hello from your AIOO card")}`} data-testid="card-email-btn"
              onClick={() => trackEvent("pitch_card_email")}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-emerald-400/40 text-emerald-300 text-sm hover:bg-emerald-400/10 transition shadow-[0_0_20px_rgba(52,211,153,0.2)]">
              <Mail className="w-4 h-4" /> Email Priyanka
            </a>
          )}
          <button onClick={enterObservatory} data-testid="card-enter-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-medium transition hover:brightness-110 shadow-[0_0_24px_rgba(139,92,246,0.45)]"
            style={{ background: "linear-gradient(90deg, #6366f1, #8b5cf6)" }}>
            Explore the Observatory <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {/* QR + share */}
        <div className="rounded-2xl p-[1.5px]" style={{ background: "linear-gradient(120deg, rgba(16,185,129,0.5), rgba(99,102,241,0.5), rgba(239,68,68,0.35))" }}>
          <div className="rounded-2xl bg-[#0a0f1f] p-5 md:p-6">
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <div className="p-3 bg-white rounded-2xl sm:hidden shadow-[0_0_30px_rgba(52,211,153,0.3)]">
                <QRCodeCanvas value={cardUrl} size={140} level="M" data-testid="card-qr-mobile" />
              </div>
              {/* Hidden high-res QR used for the PNG download */}
              <div ref={qrWrapRef} className="hidden" aria-hidden="true">
                <QRCodeCanvas value={cardUrl} size={1024} level="H" includeMargin />
              </div>
              <div className="flex-1 text-center sm:text-left">
                <h3 className="text-sm font-bold text-white flex items-center justify-center sm:justify-start gap-2 mb-1">
                  <QrCode className="w-4 h-4 text-emerald-400" /> Share this card
                </h3>
                <p className="text-xs text-slate-400 mb-4">Scan or share — the QR opens this exact page. Download it for slides, lock screen or printed cards.</p>
                <div className="flex flex-wrap justify-center sm:justify-start gap-2">
                  <button onClick={downloadQR} data-testid="card-qr-download-btn"
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-emerald-400/40 text-emerald-300 text-xs hover:bg-emerald-400/10 transition">
                    <Download className="w-3.5 h-3.5" /> Download QR (PNG)
                  </button>
                  <button onClick={copyLink} data-testid="card-copy-link-btn"
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-indigo-400/40 text-indigo-300 text-xs hover:bg-indigo-400/10 transition">
                    {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied ? "Copied!" : "Copy link"}
                  </button>
                  <button onClick={nativeShare} data-testid="card-share-btn"
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-violet-400/40 text-violet-300 text-xs hover:bg-violet-400/10 transition">
                    <Share2 className="w-3.5 h-3.5" /> Share
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <p className="text-center text-[10px] text-slate-600 mt-10">&copy; 2026 Priyanka Jha · AI Orientation Observatory</p>
      </div>
    </div>
  );
}
